import { skipToken, useQuery } from "@apollo/client/react";
import { Box, useTheme } from "@mui/material";
import { ArrowRight } from "@pmag/icons";
import { Typography } from "@pmag/ui-components";
import { type GQLComponentGroupOverviewModelQuery, type GQLComponentGroupOverviewModelQueryVariables } from "@src/graphql.generated";
import { useGlobalState } from "@src/hooks/useGlobalState";
import { componentGroupOverviewModelQuery } from "@src/pages/componentGroupOverview/ComponentGroupOverviewPage.gql";
import { type FunctionComponent } from "react";
import { FormattedMessage } from "react-intl";

import { ArticleHeaderModelDetails } from "../articleHeaderModelDetails/ArticleHeaderModelDetails";
import {
    ArrowDownBox,
    ArrowRightBox,
    BoxButtons,
    BoxContainer,
    BoxContent,
    BoxImage,
    BoxMain,
    ModelImage,
    Root,
    SelectVehicleButton,
    SeparatorDownContainer,
    SeparatorHorizontal,
    SeparatorRightContainer,
    SeparatorVertical,
    StyledIconButton,
} from "./ArticleSubheader.styles";
import { ComponentGroupOverviewPageSkeleton } from "./ArticleSubheaderSkeleton";

export const dataTestId = {
    wrapper: "sparePartsFinder.articleSubHeader.wrapper",
    loadingSkeleton: "sparePartsFinder.articleSubHeader.loadingSkeleton",
    inlineError: "sparePartsFinder.articleSubHeader.InlineError",
    modelImage: "sparePartsFinder.articleSubHeader.image",
    modelBrandName: "sparePartsFinder.articleSubHeader.brandName",
    modelDescription: "sparePartsFinder.articleSubHeader.description",
    selectVehicleButton: "sparePartsFinder.articleSubHeader.button.selectVehicle",
};

export const ArticleSubheader: FunctionComponent = () => {
    const theme = useTheme();
    const { setModelId, setContextArticleId, contextArticleId } = useGlobalState();

    const { data, error, loading } = useQuery<GQLComponentGroupOverviewModelQuery, GQLComponentGroupOverviewModelQueryVariables>(
        componentGroupOverviewModelQuery,
        contextArticleId ? { variables: { id: contextArticleId } } : skipToken,
    );

    const model = data?.model;

    if (loading) return <ComponentGroupOverviewPageSkeleton data-testid={dataTestId.loadingSkeleton} />;

    if (error || !contextArticleId || !model) {
        return null;
    }

    const onSelectVehicleClick = () => {
        setModelId(model?.id || "");
        setContextArticleId(undefined);
    };

    return model ? (
        <Root>
            <SeparatorRightContainer>
                <SeparatorVertical />

                <ArrowRightBox />
            </SeparatorRightContainer>

            <SeparatorDownContainer>
                <SeparatorHorizontal />

                <ArrowDownBox />
            </SeparatorDownContainer>

            <BoxContainer data-testid={dataTestId.wrapper}>
                <BoxImage>{model.assetPath && <ModelImage src={model.assetPath} data-testid={dataTestId.modelImage} />}</BoxImage>

                <BoxContent>
                    <BoxMain>
                        {model.brand && (
                            <Typography variant="body-short-01" color={theme.palette.grey["600"]} data-testid={dataTestId.modelBrandName}>
                                {model.brand.name}
                            </Typography>
                        )}

                        <Typography variant="body-short-01" color={theme.palette.grey["600"]}>
                            {model.description && <Box component="span" data-testid={dataTestId.modelDescription}>{`${model.description} `}</Box>}

                            <ArticleHeaderModelDetails
                                variant="subHeader"
                                articleNumber={model.articleNumber}
                                year={model.year}
                                specifications={model.modelSpecifications.map((spec) => spec.name)}
                            />
                        </Typography>

                        <BoxButtons>
                            <SelectVehicleButton
                                startIcon={<ArrowRight size="small" />}
                                variant="text"
                                onClick={() => onSelectVehicleClick()}
                                data-testid={dataTestId.selectVehicleButton}
                            >
                                <Typography variant="body-short-01" color={theme.palette.grey["800"]}>
                                    <FormattedMessage id="sparePartsFinder.articleSubHeader.button.showModel" defaultMessage="Show model" />
                                </Typography>
                            </SelectVehicleButton>
                        </BoxButtons>
                    </BoxMain>
                </BoxContent>

                <StyledIconButton variant="transparent" onClick={onSelectVehicleClick}>
                    <ArrowRight size={16} />
                </StyledIconButton>
            </BoxContainer>
        </Root>
    ) : null;
};
